import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DEMO = [
  { email: "owner@erp4u.in", role: "Owner" },
  { email: "staff@erp4u.in", role: "Staff" },
  { email: "ca@erp4u.in", role: "CA / Viewer" },
];

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Set by the 401 interceptor so a dropped session resumes where it left off.
  const next = params.get("next");
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    email: "owner@erp4u.in", password: "Demo@1234", name: "", business_name: "", gstin: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res =
      mode === "login"
        ? await login(form.email, form.password)
        : await register({
            name: form.name, email: form.email, password: form.password,
            business_name: form.business_name, gstin: form.gstin || null,
          });
    setBusy(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Welcome back" : "Your business is set up");
      navigate(next && next.startsWith("/") ? next : "/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between p-12 text-slate-300 relative overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="relative">
          <div className="font-heading text-2xl font-bold text-white">ERP4U</div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-blue-400 mt-1">Billing · GST Compliant</div>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Invoice in under
            <span className="text-blue-400"> 30 seconds.</span>
            <br />
            Compliant every time.
          </h1>
          <p className="mt-6 text-sm leading-relaxed text-slate-400">
            Rule 46 field validation, GSTIN checksum, automatic CGST/SGST vs IGST split by place of
            supply, gap-free invoice series, and Indian amount-in-words on every document.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-xs">
            {[
              ["Gap-free", "statutory series"],
              ["CGST/SGST", "or IGST, automatic"],
              ["FIFO", "payment allocation"],
            ].map(([a, b]) => (
              <div key={a} className="border-l-2 border-blue-500 pl-3">
                <div className="font-mono text-sm text-white">{a}</div>
                <div className="text-slate-500 mt-0.5">{b}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-[11px] text-slate-600">
          Webteam Private Limited · Baner, Pune · WTPL/PRD/2026/ERP4U-BILL-002
        </div>
      </div>

      <div className="flex items-center justify-center bg-slate-50 p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <div className="font-heading text-2xl font-bold">ERP4U Billing</div>
            <div className="text-xs uppercase tracking-[0.2em] text-blue-600 mt-1">GST Compliant Invoicing</div>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">
            {mode === "login" ? "Sign in" : "Create your business"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {mode === "login" ? "Use a demo account below or your own." : "Three fields. Your first invoice in minutes."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <Field label="Your name">
                  <Input data-testid="register-name" value={form.name} onChange={set("name")} required placeholder="Manoj Warule" />
                </Field>
                <Field label="Business name">
                  <Input data-testid="register-business" value={form.business_name} onChange={set("business_name")} required placeholder="Webteam Interiors" />
                </Field>
                <Field label="GSTIN (optional)">
                  <Input data-testid="register-gstin" value={form.gstin} onChange={set("gstin")} placeholder="27AABCW1234A1Z5" className="font-mono uppercase" />
                </Field>
              </>
            )}
            <Field label="Email">
              <Input data-testid="login-email" type="email" value={form.email} onChange={set("email")} required />
            </Field>
            <Field label="Password">
              <Input data-testid="login-password" type="password" value={form.password} onChange={set("password")} required />
            </Field>

            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="auth-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-2 h-11 rounded-md"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : null}
              {mode === "login" ? "Sign in" : "Create business"}
              {!busy && <ArrowRight size={16} />}
            </Button>
          </form>

          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            data-testid="auth-toggle-mode"
            className="mt-4 text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
          >
            {mode === "login" ? "New here? Create a business account" : "Already have an account? Sign in"}
          </button>

          {mode === "login" && (
            <div className="mt-8 rounded-md border border-slate-200 bg-white p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Demo accounts</div>
              <div className="mt-2 divide-y divide-slate-100">
                {DEMO.map((d) => (
                  <button
                    key={d.email}
                    data-testid={`demo-${d.role.split(" ")[0].toLowerCase()}`}
                    onClick={() => setForm((f) => ({ ...f, email: d.email, password: "Demo@1234" }))}
                    className="w-full flex items-center justify-between py-2 text-left hover:bg-slate-50 transition-colors duration-150 px-1 rounded"
                  >
                    <span className="font-mono text-xs">{d.email}</span>
                    <span className="text-[11px] text-slate-500">{d.role}</span>
                  </button>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-slate-400 font-mono">Password: Demo@1234</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-slate-700">{label}</Label>
    {children}
  </div>
);
